import dayjs from "dayjs";
import { Card } from "../../UI/Card.tsx";
import { LoadingRows } from "../../UI/Loading/LoadingRows.tsx";
import { classNames } from "../../UI/Utils/classNames.ts";
import { formatDateRange } from "../TeacherActivity/activityFormat.ts";
import { shareOf } from "../TeacherActivity/shareOf.ts";
import { BOOKED_HOURS_COLOR } from "../TeacherActivity/teacherActivityColors.ts";
import { TeacherTierBadge } from "./TeacherTierBadge.tsx";
import { CapReachedBadge } from "./CapReachedBadge.tsx";
import { TEACHER_TIERS_FROM_BEST, TeacherTier } from "./teacherTiers.ts";
import {
  ASSIGNMENT_CAP_UPCOMING_WEEKS,
  TeacherAssignmentCapWeek,
  useTeacherAssignmentCapOverview,
} from "./useTeacherAssignmentCapOverview.ts";

const CAP_REACHED_COLOR = "#d97706";

function weekLabel(weekIndex: number): string {
  if (weekIndex === 0) return "This week";
  if (weekIndex === 1) return "Next week";
  return `In ${weekIndex} weeks`;
}

function toPercent(share: number | null): string {
  return `${Math.min(100, (share ?? 0) * 100)}%`;
}

function CapUsageBar({ week }: { week: TeacherAssignmentCapWeek }) {
  const assignedShare = shareOf(week.assignedClassRooms, week.offeredSlots);
  const capShare = shareOf(week.maxAssignableClassRooms, week.offeredSlots);
  return (
    <div className={"relative h-2.5 rounded-full bg-slate-100"}>
      <div
        className={"absolute inset-y-0 left-0 rounded-full"}
        style={{
          width: toPercent(assignedShare),
          backgroundColor: week.hasBeenReached
            ? CAP_REACHED_COLOR
            : BOOKED_HOURS_COLOR,
        }}
      />
      {week.offeredSlots > 0 && (
        <div
          title={`Cap: ${week.maxAssignableClassRooms} classes`}
          className={"absolute -top-1 -bottom-1 w-0.5 rounded bg-slate-700"}
          style={{ left: toPercent(capShare) }}
        />
      )}
    </div>
  );
}

function WeeklyCapRow({
  week,
  weekIndex,
}: {
  week: TeacherAssignmentCapWeek;
  weekIndex: number;
}) {
  return (
    <div
      className={
        "grid grid-cols-1 sm:grid-cols-[9rem_1fr_13rem] items-center gap-x-6 gap-y-2 py-3"
      }
    >
      <div className={"flex flex-col"}>
        <span className={"text-sm font-medium text-slate-700"}>
          {weekLabel(weekIndex)}
        </span>
        <span className={"text-xs text-slate-400"}>
          {formatDateRange(dayjs(week.weekStartUtc), dayjs(week.weekEndUtc))}
        </span>
      </div>
      <CapUsageBar week={week} />
      <div className={"flex items-center sm:justify-end gap-2 text-xs"}>
        {week.offeredSlots === 0 ? (
          <span className={"text-slate-400"}>No availability offered</span>
        ) : (
          <>
            <span className={"text-slate-600"}>
              <span className={"font-semibold text-slate-800"}>
                {week.assignedClassRooms}
              </span>{" "}
              of {week.offeredSlots} slots · cap {week.maxAssignableClassRooms}
            </span>
            {week.hasBeenReached && <CapReachedBadge />}
          </>
        )}
      </div>
    </div>
  );
}

function TierLegend({ currentTier }: { currentTier: TeacherTier }) {
  return (
    <div className={"flex flex-wrap gap-2"}>
      {TEACHER_TIERS_FROM_BEST.map((tierInfo) => (
        <div
          key={tierInfo.tier}
          className={classNames(
            "flex flex-1 flex-col gap-0.5 rounded-lg border px-3 py-2",
            tierInfo.tier === currentTier
              ? "border-slate-800"
              : "border-slate-100",
          )}
        >
          <TeacherTierBadge tier={tierInfo.tier} />
          <span className={"text-xs text-slate-500"}>
            {tierInfo.finalScoreRange} · cap{" "}
            {tierInfo.maxPercentOfWeeklyAvailability}%
          </span>
        </div>
      ))}
    </div>
  );
}

export function TeacherAssignmentCapCard({
  teacherId,
  isInternalTeacher,
}: {
  teacherId: string;
  isInternalTeacher: boolean;
}) {
  const { overview, loading } = useTeacherAssignmentCapOverview(teacherId);

  return (
    <Card>
      <div className={"flex flex-col gap-1"}>
        <h2
          className={
            "text-sm font-semibold text-slate-500 uppercase tracking-wide"
          }
        >
          Weekly assignment cap
        </h2>
        <span className={"text-xs text-slate-400"}>
          Current week and the next {ASSIGNMENT_CAP_UPCOMING_WEEKS - 1} weeks
        </span>
      </div>

      {loading ? (
        <LoadingRows rows={4} />
      ) : !overview ? (
        <p className={"text-sm text-slate-400"}>No data yet.</p>
      ) : (
        <div className={"flex flex-col gap-6"}>
          <div className={"flex flex-wrap items-center gap-3"}>
            <TeacherTierBadge tier={overview.tier} />
            <span className={"text-sm text-slate-600"}>
              Final score {overview.finalScore.toFixed(2)} · up to{" "}
              <span className={"font-semibold text-slate-800"}>
                {overview.maxPercentOfWeeklyAvailability}%
              </span>{" "}
              of the slots offered each week can be assigned by best score
            </span>
          </div>

          <div className={"flex flex-col divide-y divide-slate-100"}>
            {overview.weeks.map((week, weekIndex) => (
              <WeeklyCapRow
                key={week.weekStartUtc}
                week={week}
                weekIndex={weekIndex}
              />
            ))}
          </div>

          <TierLegend currentTier={overview.tier} />

          <div className={"flex flex-col gap-1.5 text-xs text-slate-400"}>
            <p>
              Offered slots are the classes already assigned plus the free
              ShiftBase slots of the week, including the past days that nobody
              booked. Weeks run Sunday to Saturday, as in the teacher area. A
              teacher who offers availability can always get at least 1 class.
              The tier follows the current final score, so it can change during
              the week.
            </p>
            <p>
              Once the cap is reached, the best score assignment skips the
              teacher unless every teacher free in that slot reached their cap
              too. The cap does not apply to internal teachers, new teachers,
              less reviewed teacher assignments and manual assignments.
              {isInternalTeacher &&
                " This teacher is internal, so they are assigned before the cap is considered."}
            </p>
          </div>
        </div>
      )}
    </Card>
  );
}
